import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Spinner } from "@/components/ui/spinner";
import { CheckIcon, ChevronDownIcon, ChevronsUpDownIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type Industry = components["schemas"]["IndustryResponse"];
type Group = components["schemas"]["MerchantCategoryGroup"];

const UNGROUPED = "Other";

const groupLabels: Record<NonNullable<Group>, string> = {
  Agriculture: "Agriculture",
  ContractedServices: "Contracted Services",
  Transportation: "Transportation",
  Utilities: "Utilities",
  RetailStores: "Retail Stores",
  Clothing: "Clothing",
  MiscellaneousRetail: "Miscellaneous Retail",
  FinancialServices: "Financial Services",
  PersonalServices: "Personal Services",
  BusinessServices: "Business Services",
  ProfessionalServices: "Professional Services",
  Government: "Government",
  Entertainment: "Entertainment",
  Travel: "Travel",
};

function groupLabelFor(group: Group | undefined): string {
  if (!group) return UNGROUPED;
  return groupLabels[group] ?? group;
}

interface IndustryPickerProps {
  value: Industry | null;
  onChange: (industry: Industry) => void;
  id?: string;
}

export function IndustryPicker({ value, onChange, id }: IndustryPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchStarted = useRef(false);

  useEffect(() => {
    if (fetchStarted.current) return;
    fetchStarted.current = true;
    async function fetchIndustries() {
      setLoading(true);
      const { data } = await api.GET("/v1/industries");
      if (data) setIndustries(data);
      setLoading(false);
    }
    fetchIndustries();
  }, []);

  const grouped = useMemo(() => {
    const map = new Map<string, Industry[]>();
    for (const industry of industries) {
      const key = groupLabelFor(industry.group);
      const list = map.get(key);
      if (list) list.push(industry);
      else map.set(key, [industry]);
    }
    // Sort group names alphabetically; push "Other" to the end.
    return Array.from(map.entries()).sort(([a], [b]) => {
      if (a === UNGROUPED) return 1;
      if (b === UNGROUPED) return -1;
      return a.localeCompare(b);
    });
  }, [industries]);

  useEffect(() => {
    if (!open || !value) return;
    const selectedGroup = groupLabelFor(value.group);
    setExpanded((prev) => {
      if (prev.has(selectedGroup)) return prev;
      const next = new Set(prev);
      next.add(selectedGroup);
      return next;
    });
  }, [open, value]);

  function toggleGroup(name: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  const isSearching = search.trim().length > 0;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Spinner className="size-4" />
        Loading industries...
      </div>
    );
  }

  return (
    <>
      <Button
        id={id}
        type="button"
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className="w-full justify-between font-normal"
        onClick={() => setOpen(!open)}
      >
        {value ? (
          <span className="truncate">
            {value.code} - {value.displayName}
          </span>
        ) : (
          <span className="text-muted-foreground">Select an industry...</span>
        )}
        <ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
      </Button>

      {open && (
        <div className="rounded-md border">
          <Command>
            <CommandInput
              placeholder="Search industries..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>No industry found.</CommandEmpty>
              {grouped.map(([groupName, items]) => {
                const isOpen = isSearching || expanded.has(groupName);
                return (
                  <div key={groupName}>
                    <button
                      type="button"
                      onClick={() => toggleGroup(groupName)}
                      disabled={isSearching}
                      className={cn(
                        "flex w-full items-center justify-between px-2 py-1.5 text-xs font-medium text-muted-foreground hover:bg-accent/50",
                        isSearching && "opacity-60",
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        <ChevronDownIcon
                          className={cn(
                            "size-3 transition-transform",
                            isOpen ? "rotate-0" : "-rotate-90",
                          )}
                        />
                        {groupName}
                      </span>
                      <span className="font-mono">{items.length}</span>
                    </button>
                    {isOpen && (
                      <CommandGroup>
                        {items.map((industry) => (
                          <CommandItem
                            key={industry.id}
                            value={`${industry.code} ${industry.displayName} ${groupName}`}
                            onSelect={() => {
                              onChange(industry);
                              setOpen(false);
                              setSearch("");
                            }}
                          >
                            <CheckIcon
                              className={cn(
                                "mr-2 size-4",
                                value?.id === industry.id
                                  ? "opacity-100"
                                  : "opacity-0",
                              )}
                            />
                            <span className="mr-2 font-mono text-xs text-muted-foreground">
                              {industry.code}
                            </span>
                            {industry.displayName}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    )}
                  </div>
                );
              })}
            </CommandList>
          </Command>
        </div>
      )}
    </>
  );
}
